import * as React from 'react';
import { Group as AriaGroup, Input as AriaInput, TextField as AriaTextField } from 'react-aria-components';

import { HelpCircleIcon, InfoCircleIcon } from '../../icons';
import { cx, sortCx } from '../../utils/cx';
import Tooltip, { TooltipTrigger } from '../Tooltip';

import HintText from './HintText';
import Label from './Label';

import type { InputProps as AriaInputProps, TextFieldProps as AriaTextFieldProps } from 'react-aria-components';

export interface IInputBaseProps extends ITextFieldProps {
  /** Tooltip message on hover. */
  tooltip?: string;
  /**
   * Input size.
   * @default "sm"
   */
  size?: 'sm' | 'md';
  /** Placeholder text. */
  placeholder?: string;
  /** Class name for the icon. */
  iconClassName?: string;
  /** Class name for the input. */
  inputClassName?: string;
  /** Class name for the input wrapper. */
  wrapperClassName?: string;
  /** Class name for the tooltip. */
  tooltipClassName?: string;
  /** Keyboard shortcut to display. */
  shortcut?: string | boolean;
  ref?: React.Ref<HTMLInputElement>;
  groupRef?: React.Ref<HTMLDivElement>;
  /** Icon component to display on the left side of the input. */
  icon?: React.ComponentType<React.HTMLAttributes<HTMLOrSVGElement>>;
}

export const InputBase = ({
  ref,
  tooltip,
  shortcut,
  groupRef,
  size = 'sm',
  isInvalid,
  isDisabled,
  icon: Icon,
  placeholder,
  wrapperClassName,
  tooltipClassName,
  inputClassName,
  iconClassName,
  // Omit this prop to avoid invalid HTML attribute warning
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isRequired: _isRequired,
  ...inputProps
}: Omit<IInputBaseProps, 'label' | 'hint'>) => {
  // Check if the input has a leading icon or tooltip
  const hasTrailingIcon = tooltip || isInvalid;
  const hasLeadingIcon = Icon;

  // If the input is inside a `TextFieldContext`, use its context to simplify applying styles
  const context = React.useContext(TextFieldContext);

  const inputSize = context?.size || size;

  const sizes = sortCx({
    sm: {
      root: cx('px-3.5 py-2.5', hasTrailingIcon && 'pr-9', hasLeadingIcon && 'pl-10'),
      iconLeading: 'left-3.5',
      iconTrailing: 'right-3.5',
      shortcut: 'pr-3',
    },
    md: {
      root: cx('px-3.5 py-2.5', hasTrailingIcon && 'pr-9.5', hasLeadingIcon && 'pl-10.5'),
      iconLeading: 'left-3.5',
      iconTrailing: 'right-3.5',
      shortcut: 'pr-3',
    },
  });

  return (
    <AriaGroup
      {...{ isDisabled, isInvalid }}
      ref={groupRef}
      className={({ isFocusWithin, isDisabled, isInvalid }) =>
        cx(
          'bg-primary shadow-xs ring-primary relative flex w-full flex-row place-content-center place-items-center rounded-xl ring-1 ring-inset transition-shadow duration-100 ease-linear',

          isFocusWithin && !isDisabled && 'ring-brand ring-2',

          // Disabled state styles
          isDisabled && 'bg-disabled_subtle ring-disabled cursor-not-allowed',
          'group-disabled:bg-disabled_subtle group-disabled:ring-disabled group-disabled:cursor-not-allowed',

          // Invalid state styles
          isInvalid && 'ring-error_subtle',
          'group-invalid:ring-error_subtle',

          // Invalid state with focus-within styles
          isInvalid && isFocusWithin && 'ring-error ring-2',
          isFocusWithin && 'group-invalid:ring-error group-invalid:ring-2',

          context?.wrapperClassName,
          wrapperClassName
        )
      }
    >
      {/* Leading icon and Payment icon */}
      {Icon && (
        <Icon
          className={cx(
            'text-fg-quaternary pointer-events-none absolute size-5',
            isDisabled && 'text-fg-disabled',
            sizes[inputSize].iconLeading,
            context?.iconClassName,
            iconClassName
          )}
        />
      )}

      {/* Input field */}
      <AriaInput
        {...(inputProps as AriaInputProps)}
        ref={ref}
        placeholder={placeholder}
        className={cx(
          'text-md text-primary outline-hidden placeholder:text-placeholder autofill:text-primary m-0 w-full bg-transparent ring-0 autofill:rounded-xl',
          isDisabled && 'text-disabled cursor-not-allowed',
          sizes[inputSize].root,
          context?.inputClassName,
          inputClassName
        )}
      />

      {/* Tooltip and help icon */}
      {tooltip && !isInvalid && (
        <Tooltip title={tooltip} placement="top">
          <TooltipTrigger
            className={cx(
              'text-fg-quaternary hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover absolute cursor-pointer transition duration-200',
              sizes[inputSize].iconTrailing,
              context?.tooltipClassName,
              tooltipClassName
            )}
          >
            <HelpCircleIcon className="size-4" />
          </TooltipTrigger>
        </Tooltip>
      )}

      {/* Invalid icon */}
      {isInvalid && (
        <InfoCircleIcon
          className={cx(
            'text-fg-error-secondary pointer-events-none absolute size-4',
            sizes[inputSize].iconTrailing,
            context?.tooltipClassName,
            tooltipClassName
          )}
        />
      )}

      {/* Shortcut */}
      {shortcut && (
        <div
          className={cx(
            'bg-linear-to-r to-bg-primary pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] from-transparent to-40% pl-8',
            sizes[inputSize].shortcut
          )}
        >
          <span
            className={cx(
              'text-quaternary ring-secondary pointer-events-none select-none rounded px-1 py-px text-xs font-medium ring-1 ring-inset',
              isDisabled && 'text-disabled bg-transparent'
            )}
            aria-hidden="true"
          >
            {typeof shortcut === 'string' ? shortcut : '⌘K'}
          </span>
        </div>
      )}
    </AriaGroup>
  );
};

InputBase.displayName = 'InputBase';

interface IBaseProps {
  /** Label text for the input */
  label?: string;
  /** Helper text displayed below the input */
  hint?: React.ReactNode;
}

interface ITextFieldProps
  extends IBaseProps,
    AriaTextFieldProps,
    Pick<IInputBaseProps, 'size' | 'wrapperClassName' | 'inputClassName' | 'iconClassName' | 'tooltipClassName'> {
  ref?: React.Ref<HTMLDivElement>;
}

const TextFieldContext = React.createContext<ITextFieldProps>({});

export const TextField = ({ className, ...props }: ITextFieldProps) => {
  return (
    <TextFieldContext.Provider value={props}>
      <AriaTextField
        {...props}
        data-input-wrapper
        className={(state) =>
          cx(
            'group flex h-max w-full flex-col items-start justify-start gap-1.5',
            typeof className === 'function' ? className(state) : className
          )
        }
      />
    </TextFieldContext.Provider>
  );
};

TextField.displayName = 'TextField';

interface IInputProps extends IInputBaseProps, IBaseProps {
  /** Whether to hide required indicator from label */
  hideRequiredIndicator?: boolean;
}

const Input = ({
  size = 'sm',
  placeholder,
  icon: Icon,
  label,
  hint,
  shortcut,
  hideRequiredIndicator,
  className,
  ref,
  groupRef,
  tooltip,
  iconClassName,
  inputClassName,
  wrapperClassName,
  tooltipClassName,
  ...props
}: IInputProps) => {
  return (
    <TextField aria-label={!label ? placeholder : undefined} {...props} className={className}>
      {({ isRequired, isInvalid, isDisabled }) => (
        <>
          {label && <Label isRequired={hideRequiredIndicator ? !hideRequiredIndicator : isRequired}>{label}</Label>}

          <InputBase
            {...{
              ref,
              groupRef,
              size,
              placeholder,
              icon: Icon,
              shortcut,
              iconClassName,
              inputClassName,
              wrapperClassName,
              tooltipClassName,
              tooltip,
              isInvalid,
              isDisabled,
            }}
          />

          {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
        </>
      )}
    </TextField>
  );
};
Input.displayName = 'Input';

export default Input;
